import { type CSSProperties, type PointerEvent, useCallback, useEffect, useRef, useState } from 'react'
import { animationsOn, SHEET_EXIT_MS } from './motion'
import { DRAG_SLOP, type DragSample, dragOffset, releaseVelocity, shouldDismiss } from './sheet-gesture'

type Gesture = { id: number; startY: number; samples: DragSample[]; height: number; active: boolean }

// Cierre animado y arrastre de una hoja inferior. `requestClose` la baja y, al terminar, llama a
// `onClose` (sin animaciones, en el acto). `dragHandlers` van en la zona que se agarra (el asa y
// la cabecera), y `style` en la hoja, que debe llevar `data-sheet` (de ella se mide la altura).
export const useSheetMotion = (onClose: () => void) => {
	const onCloseRef = useRef(onClose)
	onCloseRef.current = onClose
	const [closing, setClosing] = useState(false)
	const closingRef = useRef(false)
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const [offset, setOffset] = useState(0)
	const [dragging, setDragging] = useState(false)
	const gesture = useRef<Gesture | null>(null)

	useEffect(() => () => clearTimeout(timer.current), [])

	// Tras cerrar se vuelve al estado inicial: quien la usa puede seguir montado (y abrirla otra vez).
	const finish = useCallback(() => {
		closingRef.current = false
		setClosing(false)
		setOffset(0)
		onCloseRef.current()
	}, [])

	const requestClose = useCallback(() => {
		if (closingRef.current) return
		if (!animationsOn()) {
			finish()
			return
		}
		closingRef.current = true
		setClosing(true)
		timer.current = setTimeout(finish, SHEET_EXIT_MS)
	}, [finish])

	const dragHandlers = {
		onPointerDown: (event: PointerEvent<HTMLElement>) => {
			if (event.button !== 0 || closingRef.current) return
			const sheet = event.currentTarget.closest<HTMLElement>('[data-sheet]')
			gesture.current = {
				id: event.pointerId,
				startY: event.clientY,
				samples: [{ y: event.clientY, t: event.timeStamp }],
				height: sheet?.offsetHeight ?? 0,
				active: false,
			}
		},
		onPointerMove: (event: PointerEvent<HTMLElement>) => {
			const current = gesture.current
			if (!current || current.id !== event.pointerId) return
			const dy = event.clientY - current.startY
			if (!current.active) {
				if (Math.abs(dy) < DRAG_SLOP) return
				current.active = true
				setDragging(true)
				// Seguir el dedo aunque salga de la zona.
				try {
					event.currentTarget.setPointerCapture?.(event.pointerId)
				} catch {
					// El navegador ya no conoce ese puntero: se arrastra igual, sin captura.
				}
			}
			// Las últimas muestras bastan para la velocidad al soltar.
			current.samples = [...current.samples.slice(-3), { y: event.clientY, t: event.timeStamp }]
			setOffset(dragOffset(dy))
		},
		onPointerUp: (event: PointerEvent<HTMLElement>) => {
			const current = gesture.current
			gesture.current = null
			if (!current?.active) return
			setDragging(false)
			const end = dragOffset(event.clientY - current.startY)
			if (shouldDismiss(end, releaseVelocity(current.samples), current.height)) requestClose()
			else setOffset(0)
		},
		onPointerCancel: () => {
			gesture.current = null
			setDragging(false)
			setOffset(0)
		},
	}

	// Al cerrar, baja entera; arrastrando, sigue al dedo; al soltar sin cerrar, vuelve a su sitio.
	const style: CSSProperties = closing
		? { transform: 'translateY(100%)', transition: `transform ${SHEET_EXIT_MS}ms ease-in` }
		: { transform: `translateY(${offset}px)`, transition: dragging ? 'none' : 'transform 200ms ease-out' }

	return { requestClose, closing, dragging, offset, style, dragHandlers }
}
